import React from "react";
import { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { lazyWithReload } from "@/system/lazyWithReload";

import { Layout }             from "@/components/Layout";
import { WorkspaceLayout }    from "@/components/WorkspaceLayout";
import { PlatformRouteGuard } from "@/shell/PlatformRouteGuard";
import { AuthProvider }       from "@/auth/AuthContext";
import { I18nProvider }       from "@/i18n/I18nContext";
import { RequireAuth }        from "@/auth/RequireAuth";
import { RedirectIfAuth }     from "@/auth/RedirectIfAuth";
import { HomeRoute }         from "@/auth/HomeRoute";
import { AppErrorBoundary }   from "@/system/appErrorBoundary";
import { Toaster }            from "@/components/ui/toaster";
import { SyncConflictBanner } from "@/components/SyncConflictBanner";
import { PublicPageMain } from "@/components/PublicPageMain";

// Public pages
import LoginPage          from "./pages/LoginPage";
import SignupPage         from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AuthCallback       from "./pages/AuthCallback";
import MagicLinkPage      from "./pages/MagicLinkPage";
import JoinPage           from "./pages/JoinPage";
import DeleteAccountPage from "./pages/DeleteAccount";

// Protected pages - lazy loaded. lazyWithReload self-heals the stale-chunk-
// after-deploy failure: a missing chunk forces ONE guarded reload to fetch the
// fresh bundle, instead of stranding the user on the route error boundary.
const AdminPage         = lazyWithReload(() => import("./pages/AdminPage"), "AdminPage");
const ResetPasswordPage = lazyWithReload(() => import("./pages/ResetPasswordPage"), "ResetPasswordPage");
const CommandCenter     = lazyWithReload(() => import("./pages/CommandCenter"), "CommandCenter");
const DiagnosticPanel   = lazyWithReload(() => import("./diagnostics/DiagnosticPanel"), "DiagnosticPanel");
const DiagnosticsPage   = lazyWithReload(() => import("./pages/DiagnosticsPage"), "DiagnosticsPage");
const Projects          = lazyWithReload(() => import("./pages/Projects"), "Projects");
// AIBuilder retired - all /ai-builder routes redirect to /dashboard
const Deploy            = lazyWithReload(() => import("./pages/Deploy"), "Deploy");
const Billing           = lazyWithReload(() => import("./pages/Billing"), "Billing");
const SettingsPage      = lazyWithReload(() => import("./pages/Settings"), "Settings");
const Platform          = lazyWithReload(() => import("./pages/Platform"), "Platform");
const WhatsNew          = lazyWithReload(() => import("./pages/WhatsNew"), "WhatsNew");
const NotificationsPage = lazyWithReload(() => import("./pages/NotificationsPage"), "NotificationsPage");
const HelpCenter        = lazyWithReload(() => import("./pages/HelpCenter"), "HelpCenter");
const Support           = lazyWithReload(() => import("./pages/Support"), "Support");
const MobileBuild       = lazyWithReload(() => import("./pages/MobileBuild"), "MobileBuild");
const MarketplacePage   = lazyWithReload(() => import("./pages/Marketplace"), "Marketplace");
const CreatorStudio     = lazyWithReload(() => import("./pages/CreatorStudio"), "CreatorStudio");
const CreativeStudio    = lazyWithReload(() => import("./pages/CreativeStudio"), "CreativeStudio");
const Library           = lazyWithReload(() => import("./pages/Library"), "Library");
const ExtensionsPage    = lazyWithReload(() => import("./pages/Extensions"), "Extensions");
const Collaboration     = lazyWithReload(() => import("./pages/Collaboration"), "Collaboration");
const IntegrationsPage  = lazyWithReload(() => import("./pages/Integrations"), "Integrations");
const WebhooksPage      = lazyWithReload(() => import("./pages/Webhooks"), "Webhooks");
const TeamPage          = lazyWithReload(() => import("./pages/Team"), "Team");
const ProjectWorkspace  = lazyWithReload(() => import("./pages/ProjectWorkspace"), "ProjectWorkspace");

// Phase 11 - Pricing route (public + authenticated)
const PricingPage       = lazyWithReload(() => import("./ui/pricing/PricingPage"), "PricingPage");
// NDIP - Document Intelligence
const DocumentsPage     = lazyWithReload(() => import("./pages/Documents"), "Documents");
// MANDATE 11 - Autonomous engineering engines workspace
const EngineeringWorkspacePage = lazyWithReload(() => import("./pages/EngineeringWorkspace"), "EngineeringWorkspace");

// Phase 12 - Enterprise org admin
const OrgAdminPage      = lazyWithReload(() => import("./pages/OrgAdminPage"), "OrgAdminPage");

// Piece 3 - shared preview (public, no auth, no sidebar)
const SharedPreview     = lazyWithReload(() => import("./pages/SharedPreview"), "SharedPreview");

// Piece 5 - vanity slug preview (public, no auth, no sidebar)
const SlugPreview       = lazyWithReload(() => import("./pages/SlugPreview"), "SlugPreview");

// Platform legal documents (public; the footer and Settings link here)
const PrivacyPage       = lazyWithReload(() => import("./pages/Privacy"), "Privacy");
const TermsPage         = lazyWithReload(() => import("./pages/Terms"), "Terms");
const AcceptableUsePage = lazyWithReload(() => import("./pages/AcceptableUse"), "AcceptableUse");
const AiContentPage     = lazyWithReload(() => import("./pages/AiContentPolicy"), "AiContentPolicy");
const SecurityPolicyPage = lazyWithReload(() => import("./pages/SecurityPolicy"), "SecurityPolicy");
const CookiePolicyPage  = lazyWithReload(() => import("./pages/CookiePolicy"), "CookiePolicy");

import NotFound from "./pages/NotFound";
import { getSubdomainSlug } from "@/lib/subdomainSlug";

function Loader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <Loader2 className="animate-spin text-muted-foreground" />
    </div>
  );
}

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loader />}>{children}</Suspense>;
}

/** Alias redirect that keeps the query string: /connectors carries a
 *  ?projectId= hand-off from the workspace nav that must reach /integrations. */
function NavigateWithSearch({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

export default function App() {
  // Subdomain routing: app.nuvrahq.com/[slug] or [slug].nuvrahq.com
  // Intercept before the main router so the full auth/layout tree is skipped.
  const subdomainSlug = getSubdomainSlug();
  if (subdomainSlug) {
    return (
      <BrowserRouter>
        <AppErrorBoundary>
          <S><SlugPreview forcedSlug={subdomainSlug} /></S>
        </AppErrorBoundary>
      </BrowserRouter>
    );
  }

  return (
    <AuthProvider>
      <I18nProvider>
      <BrowserRouter>
        <AppErrorBoundary>
          {/* Platform route suppression. Wrapped around the whole table rather than repeated per
              route, because every destination below is registered twice (at /x and at /app/x) and
              a per-route guard would inevitably protect one alias and not the other. It renders
              children unchanged on the web. See src/shell/PlatformRouteGuard.tsx. */}
          <PlatformRouteGuard>
          <Routes>

            {/* ── Public ─────────────────────────────────────────── */}
            {/* Marketing to a visitor, the workspace to a customer. See src/auth/HomeRoute.tsx:
                opening the installed app on a signup pitch, every launch, for somebody who already
                has an account, was the most repeated annoyance in the product. */}
            <Route path="/"               element={<HomeRoute />} />
            <Route path="/login"          element={<RedirectIfAuth><PublicPageMain><LoginPage /></PublicPageMain></RedirectIfAuth>} />
            <Route path="/signup"         element={<RedirectIfAuth><PublicPageMain><SignupPage /></PublicPageMain></RedirectIfAuth>} />
            <Route path="/forgot-password" element={<PublicPageMain><ForgotPasswordPage /></PublicPageMain>} />
            <Route path="/auth/callback"  element={<AuthCallback />} />
            <Route path="/auth/magic"     element={<MagicLinkPage />} />
            <Route path="/join"           element={<JoinPage />} />
            {/* Google Play account deletion resource: public, no authentication required. */}
            <Route path="/delete-account" element={<DeleteAccountPage />} />
            <Route path="/reset-password" element={<S><ResetPasswordPage /></S>} />

            {/* ── Pricing - public (no auth required) ────────────── */}
            <Route path="/pricing" element={<S><PublicPageMain><PricingPage /></PublicPageMain></S>} />

            {/* Diagnostics - public, no secrets: frontend + backend build SHA, api origin,
                provider health, so a stale deploy is provable from the browser (MANDATE 28). */}
            <Route path="/diagnostics" element={<S><DiagnosticsPage /></S>} />

            {/* Platform legal (public) */}
            <Route path="/privacy" element={<S><PublicPageMain><PrivacyPage /></PublicPageMain></S>} />
            <Route path="/terms"   element={<S><PublicPageMain><TermsPage /></PublicPageMain></S>} />
            <Route path="/acceptable-use"  element={<S><PublicPageMain><AcceptableUsePage /></PublicPageMain></S>} />
            <Route path="/ai-content"      element={<S><PublicPageMain><AiContentPage /></PublicPageMain></S>} />
            <Route path="/security-policy" element={<S><PublicPageMain><SecurityPolicyPage /></PublicPageMain></S>} />
            <Route path="/cookie-policy"   element={<S><PublicPageMain><CookiePolicyPage /></PublicPageMain></S>} />

            {/* ── Shared preview - public, no auth, no sidebar ────── */}
            <Route path="/p/:projectId" element={<S><SharedPreview /></S>} />

            {/* ── Vanity slug preview - public, no auth, no sidebar ── */}
            <Route path="/app/:slug" element={<S><SlugPreview /></S>} />

            {/* ── Workspace - full-viewport layout, no main padding ────── */}
            <Route element={<RequireAuth><WorkspaceLayout /></RequireAuth>}>
              <Route path="projects/:projectId" element={<S><ProjectWorkspace /></S>} />
            </Route>

            {/* ── Protected - pathless layout avoids collision with "/" ── */}

            <Route element={<RequireAuth><Layout /></RequireAuth>}>
              <Route path="dashboard"  element={<S><CommandCenter /></S>} />
              {/* Production Diagnostic Mode: real provenance + live read evidence. */}
              <Route path="diagnostics" element={<S><DiagnosticPanel /></S>} />
              {/* Projects listing: every project the user has created, real-time
                  from useProjects. Each opens its live workspace. */}
              <Route path="projects"            element={<S><Projects /></S>} />
              {/* Legacy builder routes → Studio (project-centric architecture) */}
              <Route path="ai-builder"    element={<Navigate to="/dashboard" replace />} />
              <Route path="build"         element={<Navigate to="/dashboard" replace />} />
              <Route path="editor"        element={<Navigate to="/dashboard" replace />} />
              <Route path="neuro-venture" element={<Navigate to="/dashboard" replace />} />

              <Route path="admin"      element={<S><AdminPage /></S>} />
              <Route path="deploy"     element={<S><Deploy /></S>} />
              <Route path="billing"    element={<S><Billing /></S>} />
              <Route path="settings"   element={<S><SettingsPage /></S>} />
              <Route path="platform"      element={<S><Platform /></S>} />
              <Route path="capabilities"  element={<Navigate to="/help" replace />} />
              <Route path="whats-new"     element={<S><WhatsNew /></S>} />
              <Route path="notifications" element={<S><NotificationsPage /></S>} />
              <Route path="help"          element={<S><HelpCenter /></S>} />
              <Route path="support"       element={<S><Support /></S>} />
              {/* A conversation is a real address: it survives a refresh, it can be pasted into a
                  bug report, and the back button behaves. Mirrors documents/:id. */}
              <Route path="support/:conversationId" element={<S><Support /></S>} />
              <Route path="mobile"        element={<S><MobileBuild /></S>} />
              <Route path="marketplace"   element={<S><MarketplacePage /></S>} />
              <Route path="creator"       element={<S><CreatorStudio /></S>} />
              <Route path="extensions"    element={<S><ExtensionsPage /></S>} />
              <Route path="collaboration" element={<S><Collaboration /></S>} />
              <Route path="connectors"    element={<NavigateWithSearch to="/integrations" />} />
              <Route path="integrations"  element={<S><IntegrationsPage /></S>} />
              <Route path="webhooks"      element={<S><WebhooksPage /></S>} />
              <Route path="team"          element={<S><TeamPage /></S>} />
              {/* Pricing inside authenticated layout (shows plan in context) */}
              <Route path="pricing"    element={<S><PricingPage /></S>} />
              <Route path="creative-studio" element={<S><CreativeStudio /></S>} />
              <Route path="library" element={<S><Library /></S>} />
              <Route path="documents"   element={<S><DocumentsPage /></S>} />
              <Route path="documents/:id" element={<S><DocumentsPage /></S>} />
              <Route path="engineering"   element={<S><EngineeringWorkspacePage /></S>} />
            </Route>

            {/* Phase 12 - Enterprise org admin (outside main Layout, full-page) */}
            <Route path="/org/:orgId/admin"      element={<RequireAuth><S><OrgAdminPage /></S></RequireAuth>} />
            <Route path="/org/:orgId/admin/:tab" element={<RequireAuth><S><OrgAdminPage /></S></RequireAuth>} />

            {/* ── /app prefix workspace - full-viewport layout ──────── */}
            <Route path="/app" element={<RequireAuth><WorkspaceLayout /></RequireAuth>}>
              <Route path="projects/:projectId" element={<S><ProjectWorkspace /></S>} />
            </Route>

            <Route path="/app" element={<RequireAuth><Layout /></RequireAuth>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard"    element={<S><CommandCenter /></S>} />
              <Route path="projects"            element={<S><Projects /></S>} />
              <Route path="ai-builder"   element={<Navigate to="/app/dashboard" replace />} />
              <Route path="build"        element={<Navigate to="/app/dashboard" replace />} />
              <Route path="editor"       element={<Navigate to="/app/dashboard" replace />} />
              <Route path="deploy"       element={<S><Deploy /></S>} />
              <Route path="billing"      element={<S><Billing /></S>} />
              <Route path="settings"     element={<S><SettingsPage /></S>} />
              <Route path="platform"     element={<S><Platform /></S>} />
              <Route path="capabilities" element={<Navigate to="/help" replace />} />
              <Route path="whats-new"    element={<S><WhatsNew /></S>} />
              <Route path="notifications" element={<S><NotificationsPage /></S>} />
              <Route path="help"         element={<S><HelpCenter /></S>} />
              <Route path="support"      element={<S><Support /></S>} />
              <Route path="support/:conversationId" element={<S><Support /></S>} />
              <Route path="mobile"       element={<S><MobileBuild /></S>} />
              <Route path="marketplace"  element={<S><MarketplacePage /></S>} />
              <Route path="creator"      element={<S><CreatorStudio /></S>} />
              <Route path="extensions"   element={<S><ExtensionsPage /></S>} />
              <Route path="connectors"    element={<NavigateWithSearch to="/app/integrations" />} />
              <Route path="integrations"  element={<S><IntegrationsPage /></S>} />
              <Route path="webhooks"      element={<S><WebhooksPage /></S>} />
              <Route path="team"          element={<S><TeamPage /></S>} />
              <Route path="pricing"      element={<S><PricingPage /></S>} />
              <Route path="creative-studio" element={<S><CreativeStudio /></S>} />
              <Route path="documents"   element={<S><DocumentsPage /></S>} />
              <Route path="documents/:id" element={<S><DocumentsPage /></S>} />
              <Route path="engineering"   element={<S><EngineeringWorkspacePage /></S>} />
            </Route>

            {/* ── 404 ───────────────────────────────────────────── */}
            <Route path="*" element={<NotFound />} />

          </Routes>
          </PlatformRouteGuard>
          <Toaster />
          {/* Global, no-op until a project diverges across devices. */}
          <SyncConflictBanner />
        </AppErrorBoundary>
      </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}
